import { LoginBriefing } from '@/features/assistant';
import { toast } from 'sonner';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  ArrowRight,
  FileText,
  FolderOpen,
  Info,
  Plus,
  Search,
  Stethoscope,
  Upload,
  UserRound,
  UserRoundCheck,
} from 'lucide-react';

import { FaxStatus } from '@/features/fax';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useOrgDateFormat } from '@/lib/timezone';
import type { UploadedDocumentOut } from '../types';
import { formatCalendarDate } from '@/lib/utils/date';
import { IconAction } from '@/components/ui/icon-action';
import { resolveGeneratedDocumentTitle } from '../utils';
import { P, usePermissions } from '@/features/permissions';
import { DocumentPreview } from '../components/DocumentPreview';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DocumentViewerSheet } from '../components/DocumentViewerSheet';
import { UploadDocumentDialog } from '../components/UploadDocumentDialog';
import { usePageHeaderSetter } from '@/components/layout/PageHeaderContext';
import { AssignDocumentPopover } from '../components/AssignDocumentPopover';
import { CreatePatientFromSource } from '../components/CreatePatientFromSource';
import { UploadedDocumentInboxSheet } from '../components/UploadedDocumentInboxSheet';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { type EncounterOut, useAssignEncounter, useEncounters } from '../api/encounters-api';
import {
  type GeneratedDocumentOut,
  useAssignDocumentToPatient,
  useDocuments,
} from '../api/documents-api';
import {
  useAssignUploadedDocumentToPatient,
  useUploadedDocument,
  useUploadedDocuments,
} from '../api/uploaded-documents-api';

type View = 'all' | 'attention' | 'documents' | 'faxes' | 'assistant';
type WorkItem =
  | { kind: 'encounter'; data: EncounterOut; key: string; time: string }
  | { kind: 'generated'; data: GeneratedDocumentOut; key: string; time: string }
  | { kind: 'uploaded'; data: UploadedDocumentOut; key: string; time: string };

/** A single worklist for encounters and documents. Existing detail routes remain intact. */
export function ClinicalWorkPage({ initialView = 'all' }: { initialView?: View }) {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { formatInstant } = useOrgDateFormat();
  const setPageHeader = usePageHeaderSetter();
  const [view, setView] = useState<View>(initialView === 'assistant' ? 'all' : initialView);
  const [search, setSearch] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [uploadedViewerOpen, setUploadedViewerOpen] = useState(false);

  const canReadEncounters = can(P.READ_ENCOUNTER);
  const canReadGenerated = can(P.READ_DOCUMENT);
  // Uploaded-document endpoints (including incoming faxes) use read_document.
  // A separate unseeded permission hid these items from non-owner Admins.
  const canReadUploaded = can(P.READ_DOCUMENT);
  const encounters = useEncounters({ limit: 100, worklist: true }, canReadEncounters);
  const generated = useDocuments({ limit: 100, inbox: true }, canReadGenerated);
  const uploaded = useUploadedDocuments({ limit: 100, inbox: true }, canReadUploaded);
  const assignEncounter = useAssignEncounter();
  const assignGenerated = useAssignDocumentToPatient();
  const assignUploaded = useAssignUploadedDocumentToPatient();

  useEffect(() => setView(initialView === 'assistant' ? 'all' : initialView), [initialView]);
  useEffect(() => {
    setPageHeader({
      title: 'Inbox',
      actions: (
        <div className="flex flex-wrap items-center gap-2">
          <LoginBriefing initiallyOpen={initialView === 'assistant'} />
          {can(P.UPLOAD_DOCUMENT) && (
            <IconAction icon={Upload} variant="outline" label="Upload document" onClick={() => setUploadOpen(true)} />
          )}
          {can(P.CREATE_ENCOUNTER) && (
            <IconAction icon={Plus} label="New encounter" onClick={() => navigate({ to: '/encounters/new' })} />
          )}
        </div>
      ),
    });
    return () => setPageHeader(null);
  }, [setPageHeader, initialView, can, navigate]);

  const items = useMemo<WorkItem[]>(() => {
    const result: WorkItem[] = [];
    // Match persisted source IDs, never titles or nearby timestamps.
    const representedEncounters = new Set(
      canReadGenerated
        ? (generated.data?.data ?? []).filter((doc) => !doc.deleted_at).flatMap((doc) => doc.source_encounter_ids ?? [])
        : [],
    );
    if (canReadEncounters)
      for (const data of encounters.data?.data ?? []) {
        if (representedEncounters.has(data.id)) continue;
        result.push({ data, kind: 'encounter', time: data.created_at, key: `encounter-${data.id}` });
      }
    if (canReadGenerated)
      for (const data of generated.data?.data ?? [])
        result.push({ data, kind: 'generated', time: data.generated_at, key: `generated-${data.id}` });
    if (canReadUploaded)
      for (const data of uploaded.data?.data ?? [])
        result.push({ data, kind: 'uploaded', time: data.created_at, key: `uploaded-${data.id}` });
    return result.sort((a, b) => b.time.localeCompare(a.time));
  }, [canReadEncounters, canReadGenerated, canReadUploaded, encounters.data, generated.data, uploaded.data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      if (view === 'attention' && !requiresAttention(item)) return false;
      if (view === 'documents' && item.kind === 'encounter') return false;
      if (view === 'faxes' && !(item.kind === 'uploaded' && item.data.source?.provider === 'srfax')) return false;
      if (!q) return true;
      return [titleFor(item), sourceFor(item), item.kind === 'encounter' ? item.data.patient?.mrn : ''].some((value) =>
        value?.toLowerCase().includes(q),
      );
    });
  }, [items, view, search]);

  const selected = filtered.find((item) => item.key === selectedKey) ?? filtered[0] ?? null;
  const selectedUploadedId = selected?.kind === 'uploaded' ? selected.data.id : undefined;
  const uploadedDetail = useUploadedDocument(selectedUploadedId);
  const busy = assignEncounter.isPending || assignGenerated.isPending || assignUploaded.isPending;

  async function assign(item: WorkItem, patientId: string) {
    try {
      if (item.kind === 'encounter')
        await assignEncounter.mutateAsync({ patient_id: patientId, encounterId: item.data.id });
      else if (item.kind === 'generated')
        await assignGenerated.mutateAsync({ patient_id: patientId, documentId: item.data.id });
      else await assignUploaded.mutateAsync({ patient_id: patientId, documentId: item.data.id });
      toast.success('Matched to patient');
    } catch {
      toast.error('Could not match this item. Please try again.');
    }
  }

  const selectedIndex = selected ? filtered.findIndex((item) => item.key === selected.key) : -1;
  const queryError =
    (canReadEncounters && encounters.isError) ||
    (canReadGenerated && generated.isError) ||
    (canReadUploaded && uploaded.isError);
  const isLoading =
    (canReadEncounters && encounters.isLoading) ||
    (canReadGenerated && generated.isLoading) ||
    (canReadUploaded && uploaded.isLoading);

  return (
    <div className="flex flex-col gap-5 p-4 md:p-6">
      {view === 'faxes' && <FaxStatus />}
      <div className="flex flex-wrap items-center gap-4">
        <Tabs
          value={view}
          onValueChange={(value) => {
            setView(value as View);
            setSelectedKey(null);
          }}>
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="attention">Needs attention</TabsTrigger>
            {(canReadGenerated || canReadUploaded) && <TabsTrigger value="documents">Documents</TabsTrigger>}
            {canReadUploaded && <TabsTrigger value="faxes">Faxes</TabsTrigger>}
          </TabsList>
        </Tabs>
        <label className="relative block min-w-64 flex-1">
          <Search
            aria-hidden="true"
            className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <span className="sr-only">Search Inbox</span>
          <Input
            value={search}
            className="pl-9"
            placeholder="Search patient, MRN, document, or source"
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
      </div>
      {queryError && (
        <p role="alert" className="text-sm text-destructive">
          Some items could not be loaded. Retry this page to see the complete worklist.
        </p>
      )}
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(300px,0.9fr)_minmax(420px,1.6fr)]">
        <Card className="min-w-0 lg:h-[max(28rem,calc(100dvh-14rem))]">
          <CardHeader className="shrink-0">
            <CardTitle>Worklist</CardTitle>
            <CardDescription>{filtered.length} recent items shown</CardDescription>
          </CardHeader>
          <CardContent
            tabIndex={0}
            role="region"
            aria-label="Inbox worklist"
            className="flex min-h-0 max-h-[60dvh] flex-col gap-1 overflow-y-auto overscroll-contain lg:max-h-none lg:flex-1 [&>button]:shrink-0">
            {isLoading && <p className="py-8 text-center text-sm text-muted-foreground">Loading Inbox…</p>}
            {!isLoading && filtered.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">No items in this view.</p>
            )}
            {filtered.map((item) => (
              <button
                key={item.key}
                type="button"
                aria-pressed={selected?.key === item.key}
                className="flex w-full items-start gap-3 rounded-md p-3 text-left hover:bg-accent aria-pressed:bg-accent"
                onClick={() => setSelectedKey(item.key)}>
                {item.kind === 'encounter' ? (
                  <Stethoscope className="mt-1 size-4 shrink-0" />
                ) : (
                  <FileText className="mt-1 size-4 shrink-0" />
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{titleFor(item)}</span>
                  <span className="block text-xs text-muted-foreground">
                    {sourceFor(item)} · {formatInstant(item.time, 'MMM d, yyyy')}
                  </span>
                </span>
                {requiresAttention(item) && (
                  <Badge
                    variant={item.kind !== 'uploaded' && item.data.status === 'failed' ? 'destructive' : 'secondary'}>
                    {item.kind !== 'uploaded' && item.data.status === 'failed' ? 'Failed' : 'Review'}
                  </Badge>
                )}
              </button>
            ))}
          </CardContent>
        </Card>
        <Card className="min-w-0 lg:h-[max(28rem,calc(100dvh-14rem))]">
          <CardHeader className="shrink-0">
            <CardTitle>{selected ? titleFor(selected) : 'Select an item'}</CardTitle>
            {selected && (
              <CardDescription>
                {sourceFor(selected)} · {formatInstant(selected.time, 'MMM d, yyyy')}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent
            role="region"
            aria-label="Selected Inbox item"
            tabIndex={0}
            className="flex min-h-0 flex-col gap-4 overflow-y-auto overscroll-contain lg:flex-1 [&>*]:shrink-0">
            {selected?.kind === 'uploaded' && (
              <>
                {uploadedDetail.data?.current_revision ? (
                  <DocumentPreview
                    fileUrl={uploadedDetail.data.current_revision.file_url}
                    fileName={uploadedDetail.data.current_revision.file_name}
                    contentType={uploadedDetail.data.current_revision.content_type}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">Preview is loading or unavailable.</p>
                )}
                <div className="flex items-start justify-between gap-6">
                  <div className="flex flex-wrap gap-2">
                    {can(P.UPDATE_DOCUMENT) && (
                      <AssignDocumentPopover
                        disabled={busy}
                        trigger={<IconAction disabled={busy} icon={UserRoundCheck} label="Match patient" />}
                        onAssign={(patient) => void assign(selected, patient.id)}
                      />
                    )}
                    {!selected.data.patient_id && can(P.UPDATE_DOCUMENT) && (
                      <CreatePatientFromSource
                        key={selected.key}
                        disabled={busy}
                        kind="uploaded"
                        sourceId={selected.data.id}
                      />
                    )}
                    <IconAction
                      icon={Info}
                      variant="outline"
                      label="More details"
                      onClick={() => setUploadedViewerOpen(true)}
                    />
                    {can(P.READ_PATIENT) && (
                      <IconAction
                        icon={UserRound}
                        variant="outline"
                        label="Open patient chart"
                        disabled={!selected.data.patient_id}
                        onClick={() =>
                          navigate({ to: '/patients/$patientId', params: { patientId: selected.data.patient_id! } })
                        }
                      />
                    )}
                  </div>
                </div>
              </>
            )}
            {selected?.kind === 'generated' && (
              <>
                <p className="text-sm text-muted-foreground">
                  {selected.data.status === 'failed'
                    ? 'Report generation failed. Open the source encounter to review its audio and processing status.'
                    : selected.data.status === 'draft'
                      ? 'Draft report awaiting review.'
                      : 'Open the report to review its content and PDF.'}
                </p>
                <div className="flex items-start justify-between gap-6">
                  <div className="flex flex-wrap gap-2">
                    {can(P.UPDATE_DOCUMENT) && (
                      <AssignDocumentPopover
                        disabled={busy}
                        trigger={<IconAction disabled={busy} icon={UserRoundCheck} label="Match patient" />}
                        onAssign={(patient) => void assign(selected, patient.id)}
                      />
                    )}
                    {!selected.data.patient_id && can(P.UPDATE_DOCUMENT) && (
                      <CreatePatientFromSource
                        key={selected.key}
                        disabled={busy}
                        kind="generated"
                        sourceId={selected.data.id}
                      />
                    )}
                    <IconAction
                      icon={FolderOpen}
                      variant="outline"
                      label="Open report"
                      onClick={() => setReportOpen(true)}
                    />
                    {canReadEncounters &&
                      selected.data.source_encounter_ids?.map((id, index) => (
                        <Button
                          key={id}
                          variant="outline"
                          onClick={() => navigate({ to: '/encounters/$encounterId', params: { encounterId: id } })}>
                          {selected.data.source_encounter_ids!.length === 1
                            ? 'Audio and transcript'
                            : `Source encounter ${index + 1}`}
                        </Button>
                      ))}
                  </div>
                </div>
              </>
            )}
            {selected?.kind === 'encounter' && (
              <>
                <p className="text-sm">
                  {selected.data.patient?.name ?? 'Patient not yet matched'} ·{' '}
                  {formatCalendarDate(selected.data.encounter_date)}
                </p>
                <p className="text-sm text-muted-foreground">
                  Status: {selected.data.status}. Open the encounter to review audio, transcript, and report.
                </p>
                <div className="flex items-start justify-between gap-6">
                  <div className="flex flex-wrap gap-2">
                    {!selected.data.patient_id && can(P.UPDATE_ENCOUNTER) && (
                      <AssignDocumentPopover
                        disabled={busy}
                        trigger={<IconAction disabled={busy} icon={UserRoundCheck} label="Match patient" />}
                        onAssign={(patient) => void assign(selected, patient.id)}
                      />
                    )}
                    {!selected.data.patient_id && can(P.UPDATE_ENCOUNTER) && (
                      <CreatePatientFromSource
                        key={selected.key}
                        disabled={busy}
                        kind="encounter"
                        sourceId={selected.data.id}
                      />
                    )}
                    <IconAction
                      icon={FolderOpen}
                      variant="outline"
                      label="Open encounter"
                      onClick={() =>
                        navigate({ to: '/encounters/$encounterId', params: { encounterId: selected.data.id } })
                      }
                    />
                    {selected.data.patient_id && can(P.READ_PATIENT) && (
                      <IconAction
                        icon={UserRound}
                        variant="outline"
                        label="Open patient chart"
                        onClick={() =>
                          navigate({ to: '/patients/$patientId', params: { patientId: selected.data.patient_id! } })
                        }
                      />
                    )}
                  </div>
                </div>
              </>
            )}
            {selectedIndex >= 0 && (
              <div className="flex justify-between gap-2 pt-3">
                <IconAction
                  variant="ghost"
                  icon={ArrowLeft}
                  label="Previous"
                  disabled={selectedIndex === 0}
                  onClick={() => setSelectedKey(filtered[selectedIndex - 1].key)}
                />
                <IconAction
                  label="Next"
                  variant="ghost"
                  icon={ArrowRight}
                  disabled={selectedIndex === filtered.length - 1}
                  onClick={() => setSelectedKey(filtered[selectedIndex + 1].key)}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <div className="flex flex-wrap gap-2 text-sm">
        {canReadEncounters && (
          <Button variant="link" onClick={() => navigate({ to: '/encounters/list' })}>
            Encounter list and date filters
          </Button>
        )}
        {canReadGenerated && canReadUploaded && (
          <Button variant="link" onClick={() => navigate({ to: '/documents/list' })}>
            Document inbox list
          </Button>
        )}
      </div>
      {selected?.kind === 'generated' && (
        <DocumentViewerSheet
          key={selected.data.id}
          open={reportOpen}
          document={selected.data}
          temporal_url={selected.data.temporal_url ?? null}
          onOpenChange={setReportOpen}
        />
      )}
      <UploadedDocumentInboxSheet
        open={uploadedViewerOpen}
        documentId={selectedUploadedId}
        onOpenChange={setUploadedViewerOpen}
      />
      <UploadDocumentDialog open={uploadOpen} onOpenChange={setUploadOpen} />
    </div>
  );
}

function requiresAttention(item: WorkItem) {
  if (!item.data.patient_id) return true;
  if (item.kind === 'uploaded')
    return ['extracting', 'failed', 'pending'].includes(item.data.current_revision?.extraction_status ?? '');
  return item.data.status === 'draft' || item.data.status === 'failed';
}

function sourceFor(item: WorkItem) {
  if (item.kind === 'encounter') return item.data.encounter_type === 'telephone' ? 'Telephone encounter' : 'Encounter';
  if (item.kind === 'generated') return 'Quick Start document';
  return item.data.source?.provider === 'srfax' ? 'Incoming fax' : 'Uploaded document';
}

function titleFor(item: WorkItem) {
  if (item.kind === 'encounter')
    return item.data.patient?.name ||
      (typeof item.data.clinical_context?.title === 'string' ? item.data.clinical_context.title : '') ||
      `${item.data.encounter_type || 'Clinical'} encounter`;
  if (item.kind === 'generated') return resolveGeneratedDocumentTitle(item.data);
  return item.data.name;
}
